// Firebase Authentication Service for FarmTrackPro
import { initializeApp, deleteApp } from 'firebase/app';
import { 
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import { 
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';

class FirebaseAuthService {
  constructor() {
    this.currentUser = null;
    this.currentFarm = null;
    this.unsubscribe = null;
  }

  // Initialize authentication listener
  init() {
    return new Promise((resolve) => {
      this.unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          // Get user data from Firestore
          const userData = await this.getUserData(user.uid);
          if (userData) {
            this.currentUser = { ...user, ...userData };
            const farmData = await this.getFarmData(userData.farmId);
            this.currentFarm = farmData;
          }
        } else {
          this.currentUser = null;
          this.currentFarm = null;
        }
        resolve(this.currentUser);
      });
    });
  }

  // Register new farm with manager
  async registerFarm(farmData, managerData, invitationCode) {
    try {
      // Validate invitation code
      const isValidCode = await this.validateInvitationCode(invitationCode);
      if (!isValidCode) {
        throw new Error('Invalid invitation code');
      }

      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        managerData.email, 
        managerData.password
      );
      const user = userCredential.user;

      // Generate farm ID
      const farmId = `farm_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

      const farm = {
        id: farmId,
        name: farmData.name,
        location: farmData.location || '',
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        isActive: true,
        invitationCode,
        settings: {
          timezone: 'Europe/Bucharest',
          currency: 'RON'
        }
      };

      const userData = {
        uid: user.uid,
        farmId: farmId,
        email: managerData.email,
        name: managerData.name,
        role: 'manager',
        permissions: ['all'],
        createdAt: serverTimestamp(),
        isActive: true
      };

      // Farm, manager and code are saved together: a code can only ever found one farm.
      try {
        await updateProfile(user, { displayName: managerData.name });
        const batch = writeBatch(db);
        batch.set(doc(db, 'farms', farmId), farm);
        batch.set(doc(db, 'users', user.uid), userData);
        batch.update(doc(db, 'invitationCodes', invitationCode), { isActive: false, usedAt: serverTimestamp(), usedBy: farmId });
        await batch.commit();
      } catch (saveError) {
        // No farm was created, so the login made a moment ago must not stay behind.
        await user.delete().catch(() => signOut(auth));
        throw saveError;
      }

      this.currentUser = { ...user, ...userData };
      this.currentFarm = farm;

      return { user: this.currentUser, farm: this.currentFarm };
    } catch (error) {
      console.error('Farm registration error:', error);
      throw error;
    }
  }

  // Login user
  async login(email, password, keepLoggedIn = false) {
    try {
      // Set persistence based on keepLoggedIn option
      if (keepLoggedIn) {
        // User will stay logged in until they explicitly sign out
        await setPersistence(auth, browserLocalPersistence);
      } else {
        // User will be logged out when browser session ends
        await setPersistence(auth, browserSessionPersistence);
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Get user data from Firestore
      const userData = await this.getUserData(user.uid);
      if (!userData) {
        throw new Error('User data not found');
      }

      if (!userData.isActive) {
        throw new Error('Account is deactivated');
      }

      // Get farm data
      const farmData = await this.getFarmData(userData.farmId);
      if (!farmData || !farmData.isActive) {
        throw new Error('Farm is not active');
      }

      this.currentUser = { ...user, ...userData };
      this.currentFarm = farmData;

      return { user: this.currentUser, farm: this.currentFarm };
    } catch (error) {
      console.error('Login error:', error);
      if (auth.currentUser) await signOut(auth).catch(() => {});
      throw error;
    }
  }

  // Google Sign In
  async signInWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if user exists in our system
      const userData = await this.getUserData(user.uid);
      if (!userData) {
        throw new Error('Account not found. Please contact your farm manager.');
      }

      if (!userData.isActive) {
        throw new Error('Account is deactivated');
      }

      const farmData = await this.getFarmData(userData.farmId);
      if (!farmData || !farmData.isActive) {
        throw new Error('Farm is not active');
      }

      this.currentUser = { ...user, ...userData };
      this.currentFarm = farmData;

      return { user: this.currentUser, farm: this.currentFarm };
    } catch (error) {
      console.error('Google sign in error:', error);
      // A Google account that was not invited gets no session here.
      if (auth.currentUser) await signOut(auth).catch(() => {});
      throw error;
    }
  }

  // Create user account (by manager)
  async createUserAccount(userData, createdByManagerId) {
    try {
      const manager = await this.getUserData(createdByManagerId);
      if (!manager || manager.role !== 'manager') {
        throw new Error('Only managers can create user accounts');
      }

      // Generate temporary password
      const tempPassword = this.generateTemporaryPassword();

      // A separate Firebase connection creates the login, so the manager stays signed in
      // and their session writes the profile below, as the security rules require.
      const accountApp = initializeApp(auth.app.options, `create-user-${Date.now()}`);
      let user;
      try {
        const accountAuth = getAuth(accountApp);
        const userCredential = await createUserWithEmailAndPassword(accountAuth, userData.email, tempPassword);
        user = userCredential.user;
        await updateProfile(user, { displayName: userData.name });
        await signOut(accountAuth);
      } finally {
        await deleteApp(accountApp);
      }

      // Create user document
      const newUserData = {
        uid: user.uid,
        farmId: manager.farmId,
        email: userData.email,
        name: userData.name,
        role: userData.role,
        permissions: this.getDefaultPermissions(userData.role),
        createdAt: serverTimestamp(),
        createdBy: createdByManagerId,
        isActive: true,
        temporaryPassword: tempPassword,
        mustChangePassword: true
      };

      await setDoc(doc(db, 'users', user.uid), newUserData);

      return { ...newUserData, temporaryPassword: tempPassword };
    } catch (error) {
      console.error('User creation error:', error);
      throw error;
    }
  }

  // Reset password
  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }

  // Logout
  async logout() {
    try {
      await signOut(auth);
      this.currentUser = null;
      this.currentFarm = null;
      return true;
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  // Get current session
  getCurrentSession() {
    if (this.currentUser && this.currentFarm) {
      return { user: this.currentUser, farm: this.currentFarm };
    }
    return null;
  }

  // Get current user (for compatibility with App.js)
  async getCurrentUser() {
    if (this.currentUser && this.currentFarm) {
      return { user: this.currentUser, farm: this.currentFarm };
    }

    // Try to get current Firebase user
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        unsubscribe();
        if (user) {
          try {
            const userData = await this.getUserData(user.uid);
            if (userData && userData.isActive) {
              const farmData = await this.getFarmData(userData.farmId);
              if (farmData && farmData.isActive) {
                this.currentUser = { ...user, ...userData };
                this.currentFarm = farmData;
                resolve({ user: this.currentUser, farm: this.currentFarm });
                return;
              }
            }
          } catch (error) {
            console.error('Error getting current user:', error);
          }
        }
        resolve(null);
      });
    });
  }

  // Check permissions
  hasPermission(permission) {
    if (!this.currentUser) return false;
    if (this.currentUser.role === 'manager') return true;
    return this.currentUser.permissions?.includes(permission) || false;
  }

  // Get user data from Firestore
  async getUserData(uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      return userDoc.exists() ? userDoc.data() : null;
    } catch (error) {
      console.error('Error getting user data:', error);
      return null;
    }
  }

  // Get farm data from Firestore
  async getFarmData(farmId) {
    try {
      const farmDoc = await getDoc(doc(db, 'farms', farmId));
      return farmDoc.exists() ? farmDoc.data() : null;
    } catch (error) {
      console.error('Error getting farm data:', error);
      return null;
    }
  }

  // Get farm users
  async getFarmUsers(farmId) {
    try {
      const q = query(
        collection(db, 'users'),
        where('farmId', '==', farmId),
        where('isActive', '==', true)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => doc.data());
    } catch (error) {
      console.error('Error getting farm users:', error);
      return [];
    }
  }

  // Validate invitation code
  async validateInvitationCode(code) {
    try {
      const codeDoc = await getDoc(doc(db, 'invitationCodes', code));
      if (!codeDoc.exists()) return false;
      
      const codeData = codeDoc.data();
      return codeData.isActive && !codeData.usedAt;
    } catch (error) {
      console.error('Error validating invitation code:', error);
      return false;
    }
  }

  // Get default permissions
  getDefaultPermissions(role) {
    const permissions = {
      manager: ['all'],
      worker: [
        'fields.view',
        'fields.edit',
        'tasks.view',
        'tasks.edit',
        'inventory.view',
        'weather.view',
        'worker.interface'
      ],
      accountant: [
        'finances.view',
        'finances.edit',
        'parcels.view',
        'parcels.edit',
        'inventory.view',
        'reports.view'
      ]
    };
    return permissions[role] || [];
  }

  // Generate temporary password
  generateTemporaryPassword() {
    return 'FTP' + Math.random().toString(36).substring(2, 10).toUpperCase();
  }

  // Cleanup
  destroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }
}

export default new FirebaseAuthService(); 